import { useCallback, useEffect } from "react";
import {
  Interface,
  ReviewDiffState,
  ReviewDiffTarget,
} from "../../../pages/interface-definitions";
import { REVIEW_DIFF_TARGET } from "../../../pages/interface-definitions/InterfaceListPageConstant";
import { createMockIfDefinitionReviewDiffsResponse } from "./if-definition-review-diffs-mock";
import { GetIfDefinitionReviewDiffsRequest } from "./GetIfDefinitionReviewDiffsRequest";

export type ReviewDiffStateUpdater = (
  current: ReviewDiffState,
) => ReviewDiffState;

type UseReviewDiffParams = {
  dfnSetVerNum: string;
  pjtId: string;
  ifList: Interface[];
  reviewDiffState: ReviewDiffState;

  onReviewDiffStateChange: (updater: ReviewDiffStateUpdater) => void;

  reviewDiffResponse?: ReviewDiffState["response"];
  sendGetIfDefinitionReviewDiffsRequest?: (
    request: GetIfDefinitionReviewDiffsRequest,
  ) => void;
  mode: GetIfDefinitionReviewDiffsRequest["mode"];
};

export const useReviewDiff = ({
  dfnSetVerNum,
  pjtId,
  ifList,
  reviewDiffState,
  onReviewDiffStateChange,
  reviewDiffResponse,
  sendGetIfDefinitionReviewDiffsRequest,
  mode,
}: UseReviewDiffParams) => {
  const updateSelectionAndResponse = useCallback(
    (
      selectedTarget: ReviewDiffTarget,
      response: ReviewDiffState["response"],
    ) => {
      onReviewDiffStateChange((current) => {
        if (
          current.selectedTarget === selectedTarget &&
          current.response === response
        ) {
          return current;
        }

        return {
          ...current,
          selectedTarget,
          response,
        };
      });
    },
    [onReviewDiffStateChange],
  );

  const requestReviewDiff = useCallback(
    (value: ReviewDiffTarget, targetIfList: Interface[] = ifList) => {
      if (value === REVIEW_DIFF_TARGET.NONE) {
        updateSelectionAndResponse(value, null);
        return;
      }
      const request: GetIfDefinitionReviewDiffsRequest = {
        dfnSetVerNum,
        pjtId,
        compareTargetType: value,
        mode,
      };
      if (sendGetIfDefinitionReviewDiffsRequest) {
        updateSelectionAndResponse(value, null);

        sendGetIfDefinitionReviewDiffsRequest(request);
        return;
      }

      const mockResponse = createMockIfDefinitionReviewDiffsResponse(
        request,
        targetIfList,
      );

      updateSelectionAndResponse(value, mockResponse);
    },
    [
      dfnSetVerNum,
      pjtId,
      ifList,
      mode,
      sendGetIfDefinitionReviewDiffsRequest,
      updateSelectionAndResponse,
    ],
  );

  const onChangeReviewDiff = useCallback(
    (event: CustomEvent) => {
      const value = event.detail.value as ReviewDiffTarget;

      requestReviewDiff(value);
    },
    [requestReviewDiff],
  );

  const refreshReviewDiff = useCallback(
    (latestIfList?: Interface[]) => {
      const selectedTarget = reviewDiffState.selectedTarget;

      if (selectedTarget === REVIEW_DIFF_TARGET.NONE) {
        return;
      }

      requestReviewDiff(selectedTarget, latestIfList ?? ifList);
    },
    [reviewDiffState.selectedTarget, requestReviewDiff, ifList],
  );

  useEffect(() => {
    if (reviewDiffResponse == null) {
      return;
    }

    onReviewDiffStateChange((current) => {
      if (reviewDiffResponse.compareTargetType !== current.selectedTarget) {
        return current;
      }

      if (current.response === reviewDiffResponse) {
        return current;
      }

      return {
        ...current,
        response: reviewDiffResponse,
      };
    });
  }, [reviewDiffResponse, onReviewDiffStateChange]);

  return {
    onChangeReviewDiff,
    refreshReviewDiff,
  };
};
